import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { styled } from '@mui/system';
import { equals, uniqBy } from 'ramda';
import { Checkbox } from '@mui/material';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import HistoryRowControls from 'Components/HistoryRowControls';
import ACSearch from 'Components/ACSearch';
import { Container, Item } from 'Components/Grid';
import colors from '../../../../config/theme/colors';
import type { TSortableFields, TSortDirection } from '../../utils';
import { sortDocuments, filterByTexts, cumulateOptions, localizeCodenames, AbnormalFlagsDisplay } from '../../utils';
import PagingWrapper from 'Components/PagingWrapper';
import { sortDocuments as sortDocumentsUtility } from 'Utility/randomUtil';
import Icon from 'Components/_NewElements/Icon';
import ToolTip from 'Components/ToolTip';
import { formatPartialDate } from 'Utility/date';
import { useAppSelector } from 'Store/index';
import InfoPopover from 'Components/InfoPopover';
import { examination } from 'neuro-schemas';

const {
  config: { smaLabResultTypes },
} = examination;

const AscIcon = styled(({ ...other }) => <ArrowDropUp fontSize="large" {...other} />)({
  display: 'block !important',
  marginTop: '0.3rem',
  marginLeft: '0.5rem',
  cursor: 'pointer',
});

const DescIcon = styled(({ ...other }) => <ArrowDropDown fontSize="large" {...other} />)({
  display: 'block !important',
  marginTop: '0.3rem',
  marginLeft: '0.5rem',
  cursor: 'pointer',
});

const SortableColumn = styled('div')({
  textDecoration: 'underline',
  color: colors.primary,
  cursor: 'pointer',
});

const StyledHeaderItem = styled(({ ...other }: React.ComponentProps<typeof Item>) => <Item xs={true} {...other} />)({
  color: colors.tertiaryText,
  fontWeight: 'normal',
  padding: '0.5rem',
});

const StyledRowContainer = styled(({ ...other }: React.ComponentProps<typeof Container>) => <Container {...other} />)(
  ({ theme }) => ({
    borderRadius: '0.5rem',
    '&:hover': { backgroundColor: theme.palette.grey[200] },
  }),
);

const StyledCellItem = styled(({ ...other }: React.ComponentProps<typeof Item>) => <Item xs={true} {...other} />)({
  fontWeight: 600,
  padding: '0.5rem',
});

const NameField = ({ name }: { name: string }): React.JSX.Element => {
  return (
    <Container alignItems="center">
      <Item xs={true}>{name}</Item>
    </Container>
  );
};

const SortedFields = ({
  documents,
  startEdit,
  sortDirection,
  sortField,
  codeFilterText,
  interpCodeFilterText,
  headers,
  showLatest,
  platform,
  showPlatformRelevantOnly,
}: {
  documents: IOwnProps['documents'];
  startEdit: IOwnProps['startEdit'];
  sortDirection: TSortDirection;
  sortField: SafeExtract<keyof IExamination, 'date' | 'codeName'>;
  codeFilterText: string[];
  interpCodeFilterText: string[];
  headers: React.JSX.Element;
  showLatest?: boolean;
  platform?: Platform;
  showPlatformRelevantOnly?: boolean;
}) => {
  let sortedDocuments = documents;

  let filteredDocuments = filterByTexts(codeFilterText, ['codeName'], sortedDocuments);
  filteredDocuments = filterByTexts(interpCodeFilterText, ['abnormalFlags'], filteredDocuments);

  sortedDocuments = sortDocuments(filteredDocuments, sortField, sortDirection);

  if (showLatest) {
    // Sort by date and then pick only uniq documents by codeName
    sortedDocuments = sortedDocuments.sort((d1, d2) =>
      sortDocumentsUtility([{ type: 'date', sortField: 'date' }])(d1, d2),
    );
    sortedDocuments = uniqBy(d => d.codeName, sortedDocuments);
  }

  if (showPlatformRelevantOnly) {
    switch (platform) {
      case 'dmd':
      case 'sma': {
        sortedDocuments = sortedDocuments.filter(sDoc => sDoc.codeName && smaLabResultTypes.includes(sDoc.codeName));
      }
    }
  }

  const shouldShowHeader = (d: IDocumentWithTypeV2<'examination'>, i: number): boolean => {
    const docIndex = sortedDocuments.findIndex(fid => fid._id === d._id);

    if (i === 0) return true;
    if (sortField === 'date') return !equals(sortedDocuments[docIndex - 1]?.['date'], d.date);
    else return !equals(sortedDocuments[docIndex - 1]?.['codeName'], d.codeName);
  };

  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const [tooltipOpen, setTooltipOpen] = React.useState<string | null>(null);

  return (
    <PagingWrapper
      list={sortedDocuments}
      listItemComponent={(d, i) => (
        <StyledRowContainer key={d._id} alignItems="baseline">
          <StyledCellItem xs={2} style={{ color: colors.primary, fontSize: '1.8rem' }}>
            {sortField === 'codeName' && shouldShowHeader(d, i) && d.codeName ? (
              <NameField name={localizeCodenames(d.codeName, fm)} />
            ) : null}
            {sortField === 'date' && shouldShowHeader(d, i) ? formatPartialDate(d.date) : null}
          </StyledCellItem>

          <StyledCellItem>
            {sortField === 'codeName' && d.date && formatPartialDate(d.date)}
            {sortField === 'date' && d.codeName && <NameField name={localizeCodenames(d.codeName, fm)} />}
          </StyledCellItem>
          <StyledCellItem>{d.value && `${d.value} ${d.units ?? ''}`}</StyledCellItem>
          <StyledCellItem>{d.valueReferenceRange ?? null}</StyledCellItem>
          <StyledCellItem>
            <AbnormalFlagsDisplay abnormalFlags={d.abnormalFlags} fm={fm} info />
          </StyledCellItem>
          <Item
            xs={1.5}
            style={{
              display: 'flex',
              cursor: 'pointer',
              alignSelf: 'center',
            }}
          >
            {d.comments && (
              <ToolTip
                open={tooltipOpen === d._id}
                title={
                  <Container style={{ display: 'flex' }}>
                    <Item xs={true}>{fm('examination.comments')}</Item>
                    <Item onClick={() => setTooltipOpen(null)}>
                      <Icon icon="close" button color={'errorDefault'} />
                    </Item>
                  </Container>
                }
                description={
                  <div style={{ maxHeight: '40rem', overflow: 'scroll', minWidth: '20rem' }}>{d.comments}</div>
                }
                content={
                  <div
                    style={{ height: '100%', display: 'flex' }}
                    onClick={() =>
                      tooltipOpen === null || tooltipOpen !== d._id ? setTooltipOpen(d._id) : setTooltipOpen(null)
                    }
                  >
                    <Icon icon="assignment" />
                  </div>
                }
                disableInteractive={false}
              />
            )}
          </Item>
          <Item xs={2.2} md={1.6} style={{ alignSelf: 'center' }}>
            {
              <HistoryRowControls
                newStyle
                buttonDimensions={{ width: 8, height: 3, fontSize: 14 }}
                document={d}
                startEdit={startEdit}
                minWidth="auto"
              />
            }
          </Item>
        </StyledRowContainer>
      )}
      limitOfItemsInPage={50}
      headerComponent={headers}
      bottomControls
    />
  );
};

const ExaminationHistory = ({ documents, startEdit }: IOwnProps): React.JSX.Element | null => {
  const platform = useAppSelector((s: { session: ISessionStore }) => s.session.platforms?.selected);

  const isSmaOrDmdPlatform = !!(platform && ['sma', 'dmd'].includes(platform));
  const abnormalFlagsWithDescription: Array<string> = documents ? cumulateOptions(documents, ['abnormalFlags']) : [];

  const [sortField, setSortField] = React.useState<TSortableFields>('date');
  const [sortDirection, setSortDirection] = React.useState<TSortDirection>('descending');
  const [codeFilterText, setCodeFilterText] = React.useState<string[]>([]);
  const [interpCodeFilterText, setInterpCodeFilterText] = React.useState<string[]>([]);
  const [showLatest, setShowLatest] = React.useState<boolean>(false);
  const [showPlatformRelevantOnly, setShowPlatformRelevantOnly] = React.useState<boolean>(isSmaOrDmdPlatform);
  const [acOptions, setAcOptions] = React.useState<Array<string>>(cumulateOptions(documents, ['codeName']));

  const setSortingField = (field: TSortableFields) => (): void => {
    setSortField(field);
    if (field === 'date') {
      setSortDirection('descending');
    } else {
      setSortDirection('ascending');
    }
  };

  const setSortingDirection = (): void => {
    setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending');
  };

  const showLatestOnChange = (): void => setShowLatest(!showLatest);

  const onClickPlatformRelevant = () => {
    setShowPlatformRelevantOnly(prev => {
      if (!prev) {
        switch (platform) {
          case 'dmd':
          case 'sma': {
            setAcOptions(prevOpts => prevOpts.filter(prevOpt => smaLabResultTypes.includes(prevOpt)));
          }
        }
      } else {
        setAcOptions(cumulateOptions(documents, ['codeName']));
      }
      return !prev;
    });
  };

  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  return documents.length ? (
    <React.Fragment>
      <Container alignItems="center" style={{ height: 'auto' }}>
        <Item xs={12}>
          <Container alignItems="flex-end">
            <Item
              style={{
                paddingLeft: '0.5rem',
                marginRight: '2rem',
                marginTop: '-2rem',
                display: 'flex',
                alignItems: 'flex-end',
              }}
            >
              <ACSearch
                width={28}
                acOptions={acOptions}
                setFilter={setCodeFilterText}
                filterValue={codeFilterText}
                optionLocalizationFunction={opt => {
                  const optLoc = {
                    LRP4_antibody: 'examination.opts.LRP4_antibody',
                    ryanodine_antibody: 'examination.opts.ryanodine_antibody',
                  }[opt];
                  return optLoc ? fm(optLoc) : opt;
                }}
                textFieldPlaceholder="examination.filterByExamCode"
              />
              <div style={{ marginLeft: '2rem' }}>
                <ACSearch
                  width={35}
                  acOptions={abnormalFlagsWithDescription}
                  filterValue={interpCodeFilterText}
                  setFilter={setInterpCodeFilterText}
                  textFieldPlaceholder="examination.filterByInterpCode"
                  optionLocalizationFunction={opt => {
                    const optLoc =
                      opt &&
                      abnormalFlagsWithDescription.includes(opt) &&
                      'examination.abnormalFlagsDescription.' + opt;
                    return optLoc ? opt + ' - ' + fm(optLoc) : opt;
                  }}
                />
              </div>
            </Item>
            <Item>
              {isSmaOrDmdPlatform && (
                <Container alignItems="center">
                  <Item onClick={onClickPlatformRelevant}>
                    <Checkbox
                      checked={showPlatformRelevantOnly}
                      color={'primary'}
                      style={{ padding: 0, margin: '0 1rem 0 0' }}
                    />
                  </Item>
                  <Item onClick={onClickPlatformRelevant}>
                    <div
                      style={{
                        cursor: 'pointer',
                        color: colors.primaryText,
                        marginRight: '1rem',
                      }}
                    >
                      <FormattedMessage id="examination.showPlatformRelevants" />
                    </div>
                  </Item>
                  <Item style={{ marginRight: '2rem' }}>
                    <InfoPopover
                      color="primary"
                      size="2.4rem"
                      text={
                        <div>
                          <FormattedMessage id="examination.shownExaminations" />
                          <ul>
                            {smaLabResultTypes.map((exam, i) => {
                              return <li key={exam + i}>{exam}</li>;
                            })}
                          </ul>
                        </div>
                      }
                    />
                  </Item>
                </Container>
              )}
            </Item>
            <Item>
              {sortField === 'codeName' && (
                <Container onClick={showLatestOnChange} alignItems="center">
                  <Item>
                    <Checkbox checked={showLatest} color={'primary'} style={{ padding: 0, margin: '0 1rem 0 0' }} />
                  </Item>
                  <Item>
                    <div
                      style={{
                        cursor: 'pointer',
                        color: colors.primaryText,
                      }}
                    >
                      <FormattedMessage id="examination.showLatest" />
                    </div>
                  </Item>
                </Container>
              )}
            </Item>
          </Container>
        </Item>
      </Container>
      <div style={{ marginTop: '1rem' }}>
        {/* Map documents under headers */}
        <SortedFields
          documents={documents}
          startEdit={startEdit}
          sortDirection={sortDirection}
          sortField={sortField}
          codeFilterText={codeFilterText}
          interpCodeFilterText={interpCodeFilterText}
          showLatest={showLatest}
          platform={platform}
          showPlatformRelevantOnly={showPlatformRelevantOnly}
          headers={
            <Container alignItems="center">
              {/* Make headers */}
              <StyledHeaderItem xs={2}>
                <Container alignItems="center">
                  <Item>
                    <FormattedMessage id={sortField === 'codeName' ? 'examination.codeName' : 'general.date'} />
                  </Item>
                  <Item onClick={setSortingDirection}>
                    {sortDirection === 'descending' ? <DescIcon /> : <AscIcon />}
                  </Item>
                </Container>
              </StyledHeaderItem>
              <StyledHeaderItem>
                <SortableColumn onClick={setSortingField(sortField === 'codeName' ? 'date' : 'codeName')}>
                  <FormattedMessage id={sortField === 'codeName' ? 'general.date' : 'examination.codeName'} />
                </SortableColumn>
              </StyledHeaderItem>
              <StyledHeaderItem>
                <FormattedMessage id={'examination.value'} />
              </StyledHeaderItem>
              <StyledHeaderItem>
                <FormattedMessage id={'examination.valueReferenceRange'} />
              </StyledHeaderItem>
              <StyledHeaderItem>
                <FormattedMessage id={'examination.abnormalFlags'} />
              </StyledHeaderItem>
              <StyledHeaderItem xs={1.6}>
                <FormattedMessage id={'examination.comments'} />
              </StyledHeaderItem>
              <Item xs={2.2} md={1.6}>
                {/* Column for controls */}
              </Item>
            </Container>
          }
        />
      </div>
    </React.Fragment>
  ) : null;
};

interface IOwnProps {
  documents: IDocumentWithTypeV2<'examination'>[];
  startEdit: StartEdit;
}

export default ExaminationHistory;
